import * as React from 'react';
import { FormEvent } from 'react';
import { useDeviceType } from '@cian/ui-kit';

import { Modal } from '../Modal';
import * as styles from './styles.css';
import { Input, PhoneInput } from '../Inputs';
import { IRetrieveVacancyResponseV2 } from '../../common/repositories/job-vacancies-backend/entities/vacancies/RetrieveVacancyResponseV2';
import { ApplyHeader } from './components/ApplyHeader';
import { FormField } from '../FormField';
import { SuccessScreen } from './components/SuccessScreen';
import { IInputsState } from '../../types/form';
import { AgreementCheckbox } from '../PdaAgreementCheckbox/AgreementCheckbox';
import { Button } from '../Button';
import { ApplyError } from './components/ApplyError';
import cn from 'clsx';
import { StickyActions } from '@cian/frontend-e2e-deal-component';
import { Textarea } from '../Textarea';

interface IApplyJobModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: () => void;
  vacancy: IRetrieveVacancyResponseV2;
  inputValues: IInputsState;
  // oxlint-disable-next-line typescript/no-explicit-any
  onChange: (arg: { [name: string]: any }) => void;
  isLoading: boolean;
  isFileUploading: boolean;
  errors: { [key: string]: string };
  isSubmitted: boolean;
  children?: React.ReactNode;
  additionalFilesUploader?: React.ReactNode;
}

export const ApplyJobModal: React.FC<IApplyJobModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  vacancy,
  inputValues,
  onChange,
  isLoading,
  isFileUploading,
  errors,
  isSubmitted,
  children,
  additionalFilesUploader,
}) => {
  const deviceType = useDeviceType();

  const stickyClassNames = {
    actionArea: cn(styles['action-area'], styles[deviceType]),
    actionAreaWhenStuck: styles['action-area-when-sticky'],
    actionWrapperWhenStuck: styles['action-wrapper-when-sticky'],
  };

  const handlerChange = (name: string, value: string) => {
    onChange({ [name]: value });
  };

  const onSubmitHelper = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    onSubmit();
  };

  if (errors?.apply) {
    return (
      <Modal isOpen={isOpen} onClose={onClose} type={deviceType === 'desktop' ? 'window' : 'curtain'}>
        <ApplyError title={errors.apply} onClose={onClose} />
      </Modal>
    );
  }

  if (isSubmitted) {
    return (
      <Modal onClose={onClose} isOpen={isOpen} fullScreen={deviceType !== 'desktop'}>
        <SuccessScreen onClose={onClose} type="apply" />
      </Modal>
    );
  }

  return (
    <Modal
      header={<ApplyHeader subtitle={'Отклик на вакансию'} title={deviceType === 'desktop' ? vacancy.title : ''} />}
      onClose={onClose}
      isOpen={isOpen}
      fullScreen={deviceType !== 'desktop'}
    >
      {deviceType !== 'desktop' && <ApplyHeader title={vacancy.title} />}

      <form className={cn(styles.form, styles[deviceType])} onSubmit={e => onSubmitHelper(e)}>
        <div className={cn(styles.inputs, styles[deviceType])}>
          <div className={styles['inputs-group']}>
            <FormField errorText={errors['fullName']}>
              <Input
                invalid={!!errors['fullName']}
                placeholder="Фамилия и имя"
                value={inputValues.fullName}
                name="fullName"
                disabled={isLoading}
                onChange={value => handlerChange('fullName', value)}
              />
            </FormField>

            <FormField errorText={errors['email']}>
              <Input
                invalid={!!errors['email']}
                placeholder="Email"
                value={inputValues.email}
                name={'email'}
                disabled={isLoading}
                onChange={value => handlerChange('email', value)}
              />
            </FormField>

            <FormField errorText={errors['phone']}>
              <PhoneInput
                invalid={!!errors['phone']}
                placeholder="Телефон"
                name={'phone'}
                value={inputValues.phone}
                disabled={isLoading}
                onChange={value => handlerChange('phone', value)}
              />
            </FormField>

            <FormField errorText={errors['additionalContact']}>
              <Input
                invalid={!!errors['additionalContact']}
                placeholder="Имя в Telegram, через @"
                name="additionalContact"
                value={inputValues.additionalContact}
                disabled={isLoading}
                onChange={value => handlerChange('additionalContact', value)}
              />
            </FormField>

            <FormField errorText={errors['cvUrl']}>
              <Input
                invalid={!!errors['cvUrl']}
                placeholder="Ссылка на резюме"
                name={'cvUrl'}
                value={inputValues.cvUrl}
                disabled={isLoading}
                onChange={value => handlerChange('cvUrl', value)}
              />
            </FormField>

            {children && <FormField errorText={errors['cvFile']}>{children}</FormField>}

            {additionalFilesUploader && (
              <FormField errorText={errors['additionalFiles']}>{additionalFilesUploader}</FormField>
            )}

            <div className={styles['full-width']}>
              <FormField errorText={errors['comment']}>
                <Textarea
                  placeholder="Комментарий (необязательно)"
                  hasCounter
                  maxLength={5000}
                  name={'comment'}
                  value={inputValues.comment || ''}
                  onChange={e => handlerChange('comment', e.target.value)}
                  disabled={isLoading}
                />
              </FormField>
            </div>
          </div>

          <div className={styles.checkbox}>
            <FormField errorText={errors['agreement.fileHash']}>
              <AgreementCheckbox
                id="agreement"
                isError={!!errors['agreement.fileHash']}
                isChecked={!!inputValues.agreement}
                setChecked={val => onChange({ agreement: val })}
                personalDataProcessingPolicyUrl={vacancy.agreement.fileUrl}
                confidentialityPolicyUrl={''}
                isDisabled={isLoading}
              />
            </FormField>
          </div>
        </div>
        <StickyActions
          deviceTypesWithSticky={['tablet', 'phone']}
          position={'sticky'}
          stickyClassNames={stickyClassNames}
        >
          <div className={styles.submit}>
            <Button type="submit" fullWidth disabled={isFileUploading} loading={isLoading}>
              Отправить
            </Button>
          </div>
        </StickyActions>
      </form>
    </Modal>
  );
};

ApplyJobModal.displayName = 'ApplyJobModal';
